/**
 * SUB-4.5 · FR40. One navigator entry per workflow, not one per account.
 *
 * Thirteen client accounts provisioned from the same parent produce thirteen
 * copies of "Commercial Standard". The navigator has to show ONE entry, and the
 * board behind it shows a tab per account (FR41). This module answers only the
 * first half: which pipelines are the same workflow.
 *
 * ⚠️ THIS MODULE GROUPS. IT DOES NOT BOUND ANYTHING.
 *
 * Same rule as `accountAttribution.ts`: ISO4 says the merged bound is applied at
 * the data layer, so everything here runs after the rows have arrived and
 * nothing in this file may ever be the reason a pipeline is absent. The set it
 * groups is whatever `merged_scope_pipelines()` returned, which is
 * `merged_scope_tenant_ids()` evaluated in the database.
 *
 * ⚠️ NO FOREIGN KEY BACKS `cloned_from_pipeline_id` (brief §6, deliberately: a
 * parent may delete its own pipeline while a client's copy survives). So a
 * lineage value may point at a row that is not in the list, either because it
 * was deleted or because it belongs to an account outside the caller's scope.
 * That is NOT an error and it is NOT the same as NULL:
 *
 *     NULL                 "created here, not copied"      -> grouped by name
 *     a value, present     "copied from that one"          -> grouped with it
 *     a value, absent      "copied from something since
 *                           deleted, or out of scope"      -> STILL grouped by
 *                                                             that value
 *
 * The third case is why grouping keys on the RAW lineage value rather than on a
 * resolved origin row. Two clients copied from the same since-deleted parent
 * pipeline are still the same workflow, and resolving first would split them
 * into two name-keyed groups the day the parent tidied up.
 *
 * ⚠️ GROUPING IS ONE LEVEL DEEP, because `enforce_one_level_hierarchy` makes a
 * copy of a copy impossible. Nothing here walks a chain, and adding chain
 * walking without that constraint changing would be inventing a hierarchy the
 * database refuses to hold.
 */

/** The shape this module needs from a pipeline row. The table row is a superset. */
export interface LineagePipeline {
  id: number;
  name: string;
  pipeline_type?: string | null;
  tenant_id?: string | null;
  cloned_from_pipeline_id?: number | null;
}

/** One workflow, across every account that has a copy of it. */
export interface PipelineGroup<T extends LineagePipeline = LineagePipeline> {
  /** Stable identity, safe as a React key and as a URL value. */
  key: string;
  /** What the navigator renders. Never empty. */
  label: string;
  /** What grouped these together. Reported so the UI can explain itself. */
  basis: 'lineage' | 'name';
  /**
   * Every copy, anchor first then by id. One element is the normal case in
   * single-account scope and is not a degenerate group.
   */
  members: T[];
  /** The member the navigator links to, and the tab selected by default. */
  representative: T;
  /**
   * Lineage groups only. Whether the pipeline they were copied FROM is in the
   * list. False means "copied from something since deleted, or out of scope",
   * which is a fact worth showing and is not a failure.
   */
  originPresent: boolean;
}

/**
 * Name fallback key, for pipelines nothing was copied from and that were copied
 * from nothing.
 *
 * Case-folded and whitespace-collapsed, because "Commercial  Standard" and
 * "commercial standard" in two accounts are the same workflow by any reading a
 * user would give them, and FR40's fallback exists precisely for accounts that
 * built the same thing independently.
 */
function nameKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

/**
 * FR40. Group pipelines into one entry per workflow.
 *
 * `anchorTenantId` is the account whose copy wins the label and the default tab.
 * In merged scope that is the agency; in single-account scope it is the only
 * account present and the argument changes nothing. Pass null and the lowest id
 * decides, which is stable but arbitrary.
 *
 * ⚠️ `pipeline_type` IS PART OF EVERY KEY. AC1 says pipelines are deduplicated
 * "within their existing type groupings", and the name fallback is what makes
 * that load-bearing: a commercial "Standard" and a residential "Standard" are
 * two workflows with one name, and a group spanning both would put a residential
 * board behind a commercial navigator entry. The caller usually splits by type
 * before calling this; the key means it does not have to have remembered to.
 */
export function groupPipelinesByLineage<T extends LineagePipeline>(
  pipelines: readonly T[] | null | undefined,
  anchorTenantId?: string | null,
): PipelineGroup<T>[] {
  const rows = (pipelines ?? []).filter((p): p is T => !!p && typeof p.id === 'number');

  // Which pipelines are somebody's origin. An origin joins the group of its own
  // copies rather than sitting in a name-keyed group of its own.
  const referencedOrigins = new Set<number>();
  for (const row of rows) {
    if (typeof row.cloned_from_pipeline_id === 'number') {
      referencedOrigins.add(row.cloned_from_pipeline_id);
    }
  }

  const buckets = new Map<string, { basis: 'lineage' | 'name'; origin: number | null; members: T[] }>();

  for (const row of rows) {
    const type = (row.pipeline_type ?? '').trim().toLocaleLowerCase();
    const origin =
      typeof row.cloned_from_pipeline_id === 'number'
        ? row.cloned_from_pipeline_id
        : referencedOrigins.has(row.id)
          ? row.id
          : null;

    const basis: 'lineage' | 'name' = origin === null ? 'name' : 'lineage';
    const key = origin === null ? `${type}|name:${nameKey(row.name ?? '')}` : `${type}|lineage:${origin}`;

    const bucket = buckets.get(key);
    if (bucket) bucket.members.push(row);
    else buckets.set(key, { basis, origin, members: [row] });
  }

  const groups: PipelineGroup<T>[] = [];
  for (const [key, bucket] of buckets) {
    const members = [...bucket.members].sort((a, b) => {
      const aAnchor = anchorTenantId != null && a.tenant_id === anchorTenantId;
      const bAnchor = anchorTenantId != null && b.tenant_id === anchorTenantId;
      if (aAnchor !== bAnchor) return aAnchor ? -1 : 1;
      return a.id - b.id;
    });

    // The label comes from the anchor's copy when there is one, then from the
    // origin, then from the lowest id. That is what AC2 turns on: a sub-account
    // that RENAMED its copy stays in this group and does not get to rename the
    // navigator entry for everyone.
    const representative =
      members.find((m) => anchorTenantId != null && m.tenant_id === anchorTenantId) ??
      (bucket.origin !== null ? members.find((m) => m.id === bucket.origin) : undefined) ??
      members[0];

    groups.push({
      key,
      label: (representative.name ?? '').trim() || 'Untitled pipeline',
      basis: bucket.basis,
      members,
      representative,
      originPresent: bucket.origin !== null && rows.some((r) => r.id === bucket.origin),
    });
  }

  return groups.sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key));
}

/**
 * FR41. The group a given pipeline belongs to, so the board can build its tab
 * strip from the same rule the navigator grouped with.
 *
 * The board re-derives this rather than being handed it through the URL. A
 * navigator that passed a group id would be a second place the rule is spelled,
 * and the two would drift the first time one of them was edited.
 */
export function groupContaining<T extends LineagePipeline>(
  groups: readonly PipelineGroup<T>[],
  pipelineId: number | null | undefined,
): PipelineGroup<T> | null {
  if (pipelineId == null) return null;
  return groups.find((g) => g.members.some((m) => m.id === pipelineId)) ?? null;
}

/**
 * The member of a group belonging to a given account, for switching tabs.
 *
 * Returns null rather than a fallback: an account with no copy of this workflow
 * has no tab, and quietly substituting another account's pipeline is exactly the
 * mis-attribution FR34 and FR38 exist to prevent.
 */
export function memberForAccount<T extends LineagePipeline>(
  group: PipelineGroup<T> | null | undefined,
  tenantId: string | null | undefined,
): T | null {
  if (!group || !tenantId) return null;
  return group.members.find((m) => m.tenant_id === tenantId) ?? null;
}
